import { useState } from 'react';
import { Button, Spin } from 'antd';
import styles from './DisclaimerModal.module.scss';
import {
  acknowledgeDisclaimer,
  setDisclaimerAcknowledged,
  DisclaimerDto,
} from '../../apis/DisclaimerService';

interface DisclaimerModalProps {
  open: boolean;
  disclaimerData: DisclaimerDto | null;
  /** Omit for a read-only view (e.g. Help/About/Settings). */
  onAcknowledge?: () => void;
}

const DisclaimerModal = ({ open, disclaimerData, onAcknowledge }: DisclaimerModalProps) => {
  const [acknowledging, setAcknowledging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const isLoading = !disclaimerData;
  // Hide Acknowledge button when already acknowledged server-side, or when used read-only
  const showAcknowledgeButton = !!onAcknowledge && !disclaimerData?.acknowledgedAt;

  const handleAcknowledge = async () => {
    if (!disclaimerData || !onAcknowledge) return;
    setAcknowledging(true);
    setError(null);
    try {
      await acknowledgeDisclaimer(disclaimerData.id);
      setDisclaimerAcknowledged();
      onAcknowledge();
    } catch {
      setError('Failed to record acknowledgment. Please try again.');
    } finally {
      setAcknowledging(false);
    }
  };

  return (
    <div className={styles.overlay} data-testid="disclaimer-overlay">
      <div className={styles.dialog} data-testid="disclaimer-dialog">
        <div className={styles.titleBar} data-testid="disclaimer-title">
          <span className={styles.title}>{disclaimerData?.title ?? 'Disclaimer'}</span>
        </div>
        <div className={styles.body} data-testid="disclaimer-body">
          {isLoading && <Spin data-testid="disclaimer-loading" />}
          {error && <p data-testid="disclaimer-error">{error}</p>}
          {!isLoading && !error && <p>{disclaimerData.content}</p>}
        </div>
        {showAcknowledgeButton && (
          <div className={styles.footer}>
            <Button
              type="primary"
              onClick={handleAcknowledge}
              data-testid="disclaimer-acknowledge-btn"
              className={styles.acknowledgeButton}
              disabled={isLoading || acknowledging || !!error}
            >
              Acknowledge
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DisclaimerModal;
